/**
 * Module Double Poutre - API Client
 * 
 * Reprend exactement la même logique que StructureBaseAPI
 * Communication avec le serveur pour la génération des montants et poutres
 */

// Suppression de la dépendance APIManager - utilisation de fetch directement
import {
  ParametresDoublePoutre,
  MontantDoublePoutre,
  StructureDoublePoutre,
  DoublePoutreAPIResponse,
  PoutreDoublePoutre
} from '../types';
import { GeometrieForme } from '../../structure-base/types';

/**
 * Classe API pour le module Double Poutre
 * Isolation totale des erreurs selon cahier des charges
 */
class DoublePoutreModuleAPI {
  private readonly baseUrl = '/api/double-poutre';
  private readonly moduleId = 'double-poutre';

  /**
   * Génération des montants de la double poutre
   */
  async genererMontants(
    formeBase: GeometrieForme,
    hauteurStructurePrincipale: number,
    hauteurDoublePoutre: number,
    nombreMontants: number = 8
  ): Promise<{ montants: MontantDoublePoutre[]; nombreMontants: number }> {
    try {
      // Forme envoyée pour génération montants
      const response = await fetch(`${this.baseUrl}/montants`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          nombreMontants,
          formeBase,
          hauteurStructurePrincipale,
          hauteurDoublePoutre
        })
      });

      if (!response.ok) {
        throw new Error(`Erreur API montants double poutre: ${response.status}`);
      }

      const data: DoublePoutreAPIResponse = await response.json();
      
      if (!data.success || !data.montants) {
        throw new Error(data.error || 'Erreur génération montants double poutre');
      }

      return {
        montants: data.montants,
        nombreMontants: data.nombreMontants || data.montants.length
      };

    } catch (error) {
      // Erreur génération montants
      throw error;
    }
  }

  /**
   * Génération des poutres de la double poutre
   */
  async genererPoutres(
    formeBase: GeometrieForme,
    hauteurStructurePrincipale: number,
    hauteurDoublePoutre: number,
    epaisseur: number = 0.1
  ): Promise<{ poutreBasse: PoutreDoublePoutre; poutreHaute: PoutreDoublePoutre }> {
    try {
      // Forme envoyée pour génération poutres
      const response = await fetch(`${this.baseUrl}/poutres`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          formeBase,
          hauteurStructurePrincipale,
          hauteurDoublePoutre,
          epaisseur
        })
      });

      if (!response.ok) {
        throw new Error(`Erreur API poutres double poutre: ${response.status}`);
      }

      const data: DoublePoutreAPIResponse = await response.json();
      
      if (!data.success || !data.poutres) {
        throw new Error(data.error || 'Erreur génération poutres double poutre');
      }

      return data.poutres;

    } catch (error) {
      // Erreur génération poutres
      throw error;
    }
  }
}

// Instance singleton
export const doublePoutreAPI = new DoublePoutreModuleAPI();

/**
 * Fonction principale pour appeler l'API de génération de double poutre
 * Appels directs sans cache pour une réactivité maximale
 */
export async function callDoublePoutreAPI(
  parametres: ParametresDoublePoutre,
  formeBase: GeometrieForme,
  hauteurStructurePrincipale: number
): Promise<StructureDoublePoutre> {
  try {
    // Appel API double poutre

    // Appels parallèles pour optimiser les performances
    const [montantsResult, poutresResult] = await Promise.all([
      doublePoutreAPI.genererMontants(
        formeBase,
        hauteurStructurePrincipale,
        parametres.hauteur,
        parametres.nombreMontants
      ),
      doublePoutreAPI.genererPoutres(
        formeBase,
        hauteurStructurePrincipale,
        parametres.hauteur,
        parametres.epaisseur
      )
    ]);

    const result = {
      forme: formeBase,
      poutres: poutresResult,
      montants: montantsResult.montants,
      parametres,
      nombreMontants: montantsResult.nombreMontants
    };

    return result;

  } catch (error) {
    // Erreur lors de l'appel API
    return {
      forme: formeBase,
      poutres: { 
        poutreBasse: { id: 'poutre-basse-error', chemin: [], hauteur: 0, epaisseur: 0, type: 'poutre-double' } as PoutreDoublePoutre, 
        poutreHaute: { id: 'poutre-haute-error', chemin: [], hauteur: 0, epaisseur: 0, type: 'poutre-double' } as PoutreDoublePoutre 
      },
      montants: [],
      parametres,
      nombreMontants: 0,
      error: `Erreur API double poutre: ${error instanceof Error ? error.message : 'Erreur inconnue'}`
    };
  }
}
